/**
 * Edit Structure Tool — Factory
 *
 * Creates a RuntimeToolDefinition for structural workbook changes
 * (sheets, rows, columns, range operations).
 */
import { ToolBase, ToolDescription } from "../../../agents/base";
import { buildWriteEntry, collectImagesFromResult, } from "../../writeTracking";
import { EditStructureParamsSchema, StructureOperationSchema, } from "./editStructure.schema";
// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT DESCRIPTION
// ═══════════════════════════════════════════════════════════════════════════
const SYSTEM_PROMPT_DESCRIPTION = `### editStructure
Apply structural workbook changes (sheets, rows, columns, range moves).

**Supported operations:**
- \`insert_sheet\` / \`copy_sheet\` / \`copy_sheet_structure\` / \`delete_sheet\` / \`rename_sheet\` / \`move_sheet\` / \`set_sheet_tab_color\`
- \`insert_rows\` / \`insert_columns\` / \`delete_rows\` / \`delete_columns\` (bounded variants supported via \`targetRange\`; inserts also support \`direction: "at" | "after"\`)
- \`group_rows\` / \`ungroup_rows\` / \`group_columns\` / \`ungroup_columns\` / \`set_outline_level\` / \`set_gridlines\`
- \`copy_range\` (single or multi-destination, \`pasteType\` aware) / \`move_range\` / \`clear_range\` (supports \`clearType\`)

Each operation is an object with \`operation\` (name) and \`params\` (details).
Operations are applied in array order.`;
const STREAM_BATCH_SIZE = 1;
const STREAM_FLUSH_MS = 200;
const STREAM_LOG_PREFIX = "[EditStructureStream]";
// Hard toggle for latency A/B testing.
// Set to false to force single-shot editStructure calls.
const ENABLE_EDIT_STRUCTURE_STREAMING = true;
// ═══════════════════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════════════════
function asObject(value) {
    return value && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function unwrapRequest(raw) {
    const obj = asObject(raw);
    const nested = obj.request;
    if (nested && typeof nested === "object" && !Array.isArray(nested)) {
        return nested;
    }
    return obj;
}
function resolveSessionId(raw, fallback) {
    const obj = asObject(raw);
    return (typeof obj.sessionId === "string" ? obj.sessionId : undefined) || fallback;
}
// ═══════════════════════════════════════════════════════════════════════════
// TOOL FACTORY
// ═══════════════════════════════════════════════════════════════════════════
export function createEditStructureTool(runner, fallbackSessionId) {
    return new EditStructureTool(runner, fallbackSessionId).toRuntimeToolDefinition();
}
export class EditStructureTool extends ToolBase {
    constructor(runner, fallbackSessionId) {
        super();
        this.runner = runner;
        this.fallbackSessionId = fallbackSessionId;
        this.name = "editStructure";
        this.description = new ToolDescription("editStructure", "Apply structural workbook changes (sheets, rows, columns, range moves).", EditStructureParamsSchema);
        this.paramsSchema = EditStructureParamsSchema;
        this.systemPromptDescription = SYSTEM_PROMPT_DESCRIPTION;
        this.fieldsToCheck = [
            { name: "uiDescription", type: "string", list_status: false, order: 0 },
        ];
        this.listsToCheck = [
            {
                name: "operations",
                type: "array",
                item_type: "object",
                list_status: true,
                order: 1,
                check_sub_field: "operation",
            },
        ];
        this.flushQueue = Promise.resolve();
        this.bufferedOperations = [];
        this.streamedRunnerResults = [];
        this.usedStreaming = false;
        this.streamingDisabledWarned = false;
    }
    setSessionId(sessionId) {
        this.fallbackSessionId = sessionId;
    }
    reset() {
        super.reset();
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = undefined;
        }
        this.flushQueue = Promise.resolve();
        this.bufferedOperations = [];
        this.streamedRunnerResults = [];
        this.usedStreaming = false;
        this.streamError = undefined;
        this.streamingDisabledWarned = false;
    }
    addStep(fieldName, value) {
        super.addStep(fieldName, value);
        if (fieldName !== "operations" || !value || typeof value !== "object") {
            return;
        }
        if (!this.isStreamingEnabled()) {
            if (!this.streamingDisabledWarned) {
                console.log(`${STREAM_LOG_PREFIX} Streaming disabled (ENABLE_EDIT_STRUCTURE_STREAMING=false); ignoring incremental structure ops`);
                this.streamingDisabledWarned = true;
            }
            return;
        }
        if (this.streamError) {
            return;
        }
        const parsed = StructureOperationSchema.safeParse(value);
        if (!parsed.success) {
            console.warn(`${STREAM_LOG_PREFIX} Ignoring invalid streamed operation item`);
            return;
        }
        this.queueOperation(parsed.data);
    }
    isStreamingEnabled() {
        return ENABLE_EDIT_STRUCTURE_STREAMING;
    }
    queueOperation(op) {
        this.usedStreaming = true;
        this.bufferedOperations.push(op);
        if (this.bufferedOperations.length >= STREAM_BATCH_SIZE) {
            this.scheduleFlush(0);
        }
        else {
            this.scheduleFlush(STREAM_FLUSH_MS);
        }
    }
    scheduleFlush(delayMs) {
        if (this.flushTimer) {
            if (delayMs === 0) {
                clearTimeout(this.flushTimer);
                this.flushTimer = undefined;
            }
            else {
                return;
            }
        }
        if (delayMs === 0) {
            this.flushPendingOperations();
            return;
        }
        this.flushTimer = setTimeout(() => {
            this.flushTimer = undefined;
            this.flushPendingOperations();
        }, delayMs);
    }
    flushPendingOperations() {
        if (this.streamError || this.bufferedOperations.length === 0) {
            return;
        }
        const opsToSend = this.bufferedOperations.splice(0, this.bufferedOperations.length);
        this.flushQueue = this.flushQueue.then(() => this.flushOperations(opsToSend));
    }
    async flushOperations(ops) {
        if (!ops.length || this.streamError) {
            return;
        }
        try {
            const result = await this.runner.editStructure({ operations: ops }, { sessionId: this.fallbackSessionId });
            this.streamedRunnerResults.push(result);
            if (!result.success) {
                this.streamError = result.errors?.[0] || "Streaming editStructure call failed.";
                console.warn(`${STREAM_LOG_PREFIX} Flush failed: ${this.streamError}`);
            }
        }
        catch (error) {
            this.streamError = error instanceof Error ? error.message : String(error);
            console.warn(`${STREAM_LOG_PREFIX} Flush error: ${this.streamError}`);
        }
    }
    buildStreamingResult() {
        const data = [];
        const warnings = [];
        const errors = [];
        let success = true;
        let durationMs = 0;
        let operationCount = 0;
        let usedBackendFallback = false;
        let timestamp = new Date().toISOString();
        for (const result of this.streamedRunnerResults) {
            success = success && result.success;
            if (Array.isArray(result.data)) {
                data.push(...result.data);
            }
            else if (result.data !== undefined) {
                data.push(result.data);
            }
            if (result.warnings?.length)
                warnings.push(...result.warnings);
            if (result.errors?.length)
                errors.push(...result.errors);
            durationMs += result.telemetry?.durationMs ?? 0;
            operationCount += result.telemetry?.operationCount ?? 0;
            usedBackendFallback = usedBackendFallback || Boolean(result.telemetry?.usedBackendFallback);
            if (result.telemetry?.timestamp)
                timestamp = result.telemetry.timestamp;
        }
        return {
            success,
            data,
            warnings: warnings.length > 0 ? warnings : undefined,
            errors: errors.length > 0 ? errors : undefined,
            telemetry: {
                operationCount: operationCount > 0 ? operationCount : undefined,
                durationMs,
                usedBackendFallback: usedBackendFallback || undefined,
                timestamp,
            },
        };
    }
    attachWriteTracking(result) {
        const data = asObject(result.data);
        const allImages = [];
        const sheetImages = data.sheet_images_base64;
        const sheetImageNames = data.sheet_image_names;
        if (Array.isArray(sheetImages)) {
            for (let i = 0; i < sheetImages.length; i++) {
                allImages.push({
                    base64: sheetImages[i],
                    label: sheetImageNames?.[i] || "structure",
                });
            }
        }
        else if (typeof data.sheet_image_base64 === "string") {
            allImages.push({ base64: data.sheet_image_base64, label: "structure" });
        }
        const { imageIds, rawImages } = collectImagesFromResult(allImages, "structure");
        const excelReadInfos = [];
        if (imageIds.length > 0) {
            for (let i = 0; i < imageIds.length; i++) {
                const label = sheetImageNames?.[i] || "Unknown";
                excelReadInfos.push(buildWriteEntry("content", label, "FULL_SHEET", "writeStructure", imageIds[i]));
            }
        }
        else {
            excelReadInfos.push(buildWriteEntry("content", "Unknown", "FULL_SHEET", "writeStructure"));
        }
        return {
            ...result,
            _imageIds: imageIds.length > 0 ? imageIds : undefined,
            _rawImages: rawImages.length > 0 ? rawImages : undefined,
            _excelReadInfos: excelReadInfos.length > 0 ? excelReadInfos : undefined,
        };
    }
    async execute(args) {
        const sessionId = resolveSessionId(args, this.fallbackSessionId);
        const request = unwrapRequest(args);
        try {
            if (this.isStreamingEnabled() && this.usedStreaming) {
                if (this.flushTimer) {
                    clearTimeout(this.flushTimer);
                    this.flushTimer = undefined;
                }
                this.flushPendingOperations();
                await this.flushQueue;
                if (this.streamError) {
                    const partial = this.buildStreamingResult();
                    return this.attachWriteTracking({
                        ...partial,
                        success: false,
                        errors: [...(partial.errors || []), this.streamError],
                    });
                }
                return this.attachWriteTracking(this.buildStreamingResult());
            }
            const result = await this.runner.editStructure(request, { sessionId });
            return this.attachWriteTracking(result);
        }
        finally {
            this.reset();
        }
    }
}
